// components/Dashboard/tabs/platforms/YoutubeChannels.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import './YoutubeChannels.css';

const YoutubeChannels = forwardRef(({
  showMessage,
  API_URL = 'https://api.autogram.site',
  onNavigateToLibrary,
  onNavigateToAutomation
}, ref) => {
  const [youtubeChannels, setYoutubeChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingChannels, setSyncingChannels] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [channelForm, setChannelForm] = useState({
    channelId: '',
    apiKey: '',
    customName: ''
  });

  const [automationRules, setAutomationRules] = useState([]);
  const [tags, setTags] = useState([]);

  useImperativeHandle(ref, () => ({
    handleRefresh: fetchYoutubeChannels,
    handleAddChannel: () => setShowAddModal(true)
  }));

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const message = urlParams.get('message');
    const channel = urlParams.get('channel');

    if (status === 'success') {
      showMessage(
        channel ? `${channel} kanalı başarıyla eklendi!` : (message || 'İşlem başarılı'),
        'success'
      );
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === 'error') {
      showMessage(message || 'Bir hata oluştu', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    fetchYoutubeChannels();
    fetchAutomationRules();
    fetchTags();
  }, []);

  const fetchAutomationRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/automation`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setAutomationRules(response.data.rules || []);
      }
    } catch (error) {
      console.error('Otomasyon kuralları yüklenemedi:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API_URL}/tags`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setTags(response.data.tags || []);
      }
    } catch (error) {
      console.error('Etiketler yüklenemedi:', error);
    }
  };

  const findMatchingAutomationRules = (channelId) => {
    const cleanChannelId = String(channelId || '').replace('getlate_', '');
    return automationRules.filter(rule => {
      if (!rule.enable_youtube_upload || rule.is_active !== 1) return false;
      const ytId = rule.youtube_channel_id || rule.getlate_youtube_id;
      if (!ytId) return false;
      const cleanRuleId = String(ytId).replace('getlate_', '');
      return cleanChannelId === cleanRuleId ||
        channelId === ytId ||
        cleanChannelId === ytId ||
        channelId === cleanRuleId;
    });
  };

  const getChannelAutomationSchedule = (channelId) => {
    const channelRules = findMatchingAutomationRules(channelId);
    if (channelRules.length === 0) return null;

    let allTimes = [];
    channelRules.forEach(rule => {
      if (rule.daily_times) {
        try {
          const times = JSON.parse(rule.daily_times);
          if (Array.isArray(times) && times.length > 0) {
            allTimes = [...allTimes, ...times];
          }
        } catch (e) { }
      }
    });

    allTimes.sort((a, b) => a.hour !== b.hour ? a.hour - b.hour : a.minute - b.minute);

    const uniqueTimes = allTimes.filter((time, index, self) =>
      index === self.findIndex(t => t.hour === time.hour && t.minute === time.minute)
    );

    const now = new Date();
    let nextTimeIndex = uniqueTimes.findIndex(t =>
      t.hour > now.getHours() || (t.hour === now.getHours() && t.minute > now.getMinutes())
    );
    if (nextTimeIndex === -1 && uniqueTimes.length > 0) nextTimeIndex = 0;

    return { allTimes: uniqueTimes, nextTimeIndex, rulesCount: channelRules.length };
  };

  const getChannelActiveAutomationTags = (channelId) => {
    const channelRules = findMatchingAutomationRules(channelId);
    let allTagNames = [];
    channelRules.forEach(rule => {
      if (rule.tags) {
        allTagNames = [...allTagNames, ...rule.tags.split(',').map(t => t.trim())];
      }
    });
    return [...new Set(allTagNames)]
      .map(name => tags.find(tag => tag.name.toLowerCase() === name.toLowerCase()))
      .filter(Boolean);
  };

  const handleAutomationClick = (channelId, e) => {
    e.stopPropagation();
    const channelRules = findMatchingAutomationRules(channelId);
    if (channelRules.length > 0 && onNavigateToAutomation) {
      onNavigateToAutomation(channelRules[0].id);
    }
  };

  const handleTagClick = (tagId, e) => {
    e.stopPropagation();
    if (onNavigateToLibrary) onNavigateToLibrary(tagId);
  };

  const fetchYoutubeChannels = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/youtube/channels`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setYoutubeChannels(response.data.channels || []);
      }
    } catch (error) {
      console.error('YouTube kanalları yüklenemedi:', error);
      showMessage('YouTube kanalları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthConnect = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/youtube/auth/url`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }
    } catch (error) {
      console.error('OAuth bağlantı hatası:', error);
      showMessage('Bağlantı başlatılamadı', 'error');
      setLoading(false);
    }
  };

  const handleDeleteChannel = async (channelId, channelName) => {
    if (!window.confirm(`"${channelName}" kanalını kaldırmak istediğinize emin misiniz?`)) return;
    try {
      const response = await axios.delete(`${API_URL}/youtube/channels/${channelId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        showMessage('YouTube kanalı kaldırıldı', 'success');
        fetchYoutubeChannels();
      }
    } catch (error) {
      showMessage(error.response?.data?.error || 'Kanal silinemedi', 'error');
    }
  };

  const handleSyncChannel = async (channelId, channelName) => {
    if (syncingChannels.has(channelId)) {
      showMessage('Bu kanal zaten senkronize ediliyor...', 'warning');
      return;
    }
    setSyncingChannels(prev => new Set([...prev, channelId]));
    showMessage(`"${channelName}" senkronize ediliyor...`, 'info');
    try {
      const response = await axios.post(
        `${API_URL}/youtube/channels/${channelId}/sync`, {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        showMessage(response.data.message || 'Senkronizasyon tamamlandı', 'success');
        fetchYoutubeChannels();
      }
    } catch (error) {
      showMessage(error.response?.data?.error || 'Senkronizasyon başarısız', 'error');
    } finally {
      setSyncingChannels(prev => {
        const newSet = new Set(prev);
        newSet.delete(channelId);
        return newSet;
      });
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    const number = parseInt(num);
    if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
    if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
    return number.toLocaleString('tr-TR');
  };

  const formatLastUpdate = (dateString) => {
    if (!dateString) return 'Güncellenmemiş';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      if (diffMins < 1) return 'Şimdi';
      if (diffMins < 60) return `${diffMins} dk önce`;
      return `${diffHours} saat önce`;
    }
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  if (loading && youtubeChannels.length === 0) {
    return (
      <div className="yc-loading-state">
        <div className="yc-loading-spinner">⏳</div>
        <p>YouTube kanalları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="yc-youtube-channels-container">
      {youtubeChannels.length === 0 ? (
        <div className="yc-empty-state">
          <div className="yc-empty-icon">📺</div>
          <h3>Henüz YouTube kanalı eklenmemiş</h3>
          <p>GetLate ile YouTube kanalınızı bağlayarak otomatik video yüklemeye başlayın</p>
          <button className="yc-empty-add-btn" onClick={() => setShowAddModal(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            İlk Kanalı Ekle
          </button>
        </div>
      ) : (
        <div className="yc-channels-grid">
          {youtubeChannels.map(channel => {
            const automationData = getChannelAutomationSchedule(channel.id);
            const activeTags = getChannelActiveAutomationTags(channel.id);
            const isSyncing = syncingChannels.has(channel.id);

            return (
              <div key={channel.id} className="yc-channel-card">
                {/* HEADER */}
                <div className="yc-card-header">
                  <div className="yc-channel-avatar">
                    {channel.thumbnail ? (
                      <img src={channel.thumbnail} alt={channel.name} />
                    ) : (
                      <span>📺</span>
                    )}
                  </div>
                  <div className="yc-channel-info">
                    <div className="yc-channel-name-row">
                      <span className="yc-channel-name" title={channel.name}>
                        {channel.name}
                      </span>
                      {channel.url && (
                        <a
                          href={channel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="yc-external-link"
                          onClick={(e) => e.stopPropagation()}
                          title="YouTube'da aç"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                          </svg>
                        </a>
                      )}
                    </div>
                    {channel.handle && (
                      <a
                        href={channel.url || `https://youtube.com/${channel.handle?.startsWith('@') ? channel.handle : '@' + channel.handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="yc-channel-handle"
                      >
                        {channel.handle}
                      </a>
                    )}
                  </div>
                </div>

                {/* STATS */}
                <div className="yc-stats-row">
                  <div className="yc-stat-box">
                    <span className="yc-stat-icon">👥</span>
                    <div className="yc-stat-content">
                      <span className="yc-stat-value">{formatNumber(channel.stats?.subscribers)}</span>
                      <span className="yc-stat-label">Abone</span>
                    </div>
                  </div>
                </div>

                {/* OTOMASYON */}
                {automationData && automationData.allTimes.length > 0 ? (
                  <div
                    className="yc-automation-section"
                    onClick={(e) => handleAutomationClick(channel.id, e)}
                    title="Otomasyonu düzenlemek için tıklayın"
                  >
                    <div className="yc-automation-header-row">
                      <span className="yc-automation-icon">🤖</span>
                      <span className="yc-automation-label">
                        Her gün {automationData.allTimes.length} paylaşım
                        {automationData.rulesCount > 1 && ` · ${automationData.rulesCount} kural`}
                      </span>
                    </div>
                    <div className="yc-automation-times">
                      {automationData.allTimes.map((time, index) => (
                        <span
                          key={`${time.hour}-${time.minute}`}
                          className={`yc-time-chip ${index === automationData.nextTimeIndex ? 'yc-time-next' : ''}`}
                        >
                          {String(time.hour).padStart(2, '0')}:{String(time.minute).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="yc-automation-section yc-no-automation">
                    <span className="yc-automation-icon">🔕</span>
                    <span className="yc-no-automation-text">Otomasyon ayarlanmamış</span>
                  </div>
                )}

                {/* ETİKETLER */}
                {activeTags.length > 0 && (
                  <div className="yc-tags-section">
                    {activeTags.map(tag => (
                      <span
                        key={tag.id}
                        className="yc-tag-chip"
                        onClick={(e) => handleTagClick(tag.id, e)}
                        style={{ '--tag-color': tag.color }}
                        title={`${tag.name} etiketli içerikleri görüntüle`}
                      >
                        🏷️ {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* FOOTER */}
                <div className="yc-card-footer">
                  <div className="yc-footer-info">
                    <span className="yc-footer-profile">{channel.profileName || 'GetLate'}</span>
                    <span className="yc-footer-date">{formatLastUpdate(channel.updated_at || channel.created_at)}</span>
                  </div>
                  <div className="yc-footer-actions">
                    {channel.last_sync && (
                      <span className="yc-sync-badge" title={`Son sync: ${formatLastUpdate(channel.last_sync)}`}>
                        🔄 {formatLastUpdate(channel.last_sync)}
                      </span>
                    )}
                    {!channel.isGetLate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSyncChannel(channel.id, channel.name); }}
                        disabled={isSyncing}
                        className={`yc-action-btn yc-sync-btn ${isSyncing ? 'yc-spinning' : ''}`}
                        title="Senkronize Et"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                          <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteChannel(channel.id, channel.name); }}
                      className="yc-action-btn yc-delete-btn"
                      title="Kaldır"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {showAddModal && (
        <div className="yc-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="yc-modal-content" onClick={e => e.stopPropagation()}>
            <div className="yc-modal-header">
              <h3>YouTube Kanalı Ekle</h3>
              <button className="yc-modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <div className="yc-modal-body">
              <div className="yc-getlate-connect">
                <div className="yc-connect-icon">🚀</div>
                <h4>GetLate ile Bağlan</h4>
                <p>YouTube hesabınızı güvenli şekilde bağlayın. Token yönetimi otomatik yapılır.</p>
                <button
                  className="yc-connect-btn"
                  onClick={handleOAuthConnect}
                  disabled={loading}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                  {loading ? 'Yönlendiriliyor...' : 'YouTube ile Bağlan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

YoutubeChannels.displayName = 'YoutubeChannels';
export default YoutubeChannels;